import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Typography, TextField, Table, TableHead, TableBody,
  TableRow, TableCell, TableContainer, Paper, CircularProgress,
  Alert, Collapse, IconButton, Chip,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import api from "../../api/api";

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

// ── single month row with expandable item list ────────────────────────────────
const MonthRow = ({ row }) => {
  const [open, setOpen] = useState(false);
  const hasItems = row.items.length > 0;
  const isEmpty = row.total === 0;

  return (
    <>
      <TableRow
        sx={{
          "& > *": { borderBottom: "unset" },
          "&:nth-of-type(even)": { backgroundColor: "#f0f4f8" },
          opacity: isEmpty ? 0.45 : 1,
        }}
      >
        <TableCell sx={{ width: 36, py: 0.5 }}>
          {hasItems && (
            <IconButton size="small" onClick={() => setOpen(!open)}>
              {open ? (
                <KeyboardArrowUpIcon fontSize="small" />
              ) : (
                <KeyboardArrowDownIcon fontSize="small" />
              )}
            </IconButton>
          )}
        </TableCell>
        <TableCell sx={{ fontWeight: 500 }}>{MONTH_NAMES[row.month - 1]}</TableCell>
        <TableCell align="right">
          {row.fixed_total > 0 ? `€ ${row.fixed_total.toFixed(2)}` : "–"}
        </TableCell>
        <TableCell align="right">
          {row.onetime_total > 0 ? `€ ${row.onetime_total.toFixed(2)}` : "–"}
        </TableCell>
        <TableCell align="right">
          {row.cleaning_total > 0 ? `€ ${row.cleaning_total.toFixed(2)}` : "–"}
        </TableCell>
        <TableCell align="right" sx={{ fontWeight: 700 }}>
          {row.total > 0 ? `€ ${row.total.toFixed(2)}` : "–"}
        </TableCell>
      </TableRow>

      {hasItems && (
        <TableRow>
          <TableCell colSpan={5} sx={{ pb: 0, pt: 0 }}>
            <Collapse in={open} timeout="auto" unmountOnExit>
              <Box sx={{ mx: 3, my: 1 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {["Name", "Category", "Type", "Date", "Staff", "Amount (€)"].map((h) => (
                        <TableCell key={h} sx={{ fontWeight: "bold", fontSize: 11, py: 0.5 }}>
                          {h}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {row.items.map((item, i) => (
                      <TableRow key={i}>
                        <TableCell sx={{ fontSize: 11 }}>{item.name}</TableCell>
                        <TableCell sx={{ fontSize: 11 }}>
                          <Chip label={item.category} size="small" variant="outlined" sx={{ fontSize: 10 }} />
                        </TableCell>
                        <TableCell sx={{ fontSize: 11 }}>
                          <Chip
                            label={item.type}
                            size="small"
                            color={item.type === "fixed" ? "primary" : item.type === "cleaning" ? "warning" : "default"}
                          />
                        </TableCell>
                        <TableCell sx={{ fontSize: 11 }}>{item.date ?? "–"}</TableCell>
                        <TableCell sx={{ fontSize: 11 }}>{item.staff ?? "–"}</TableCell>
                        <TableCell align="right" sx={{ fontSize: 11 }}>
                          € {item.amount.toFixed(2)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

// ── main component ────────────────────────────────────────────────────────────
const CostsReportTab = () => {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(`/costs/reports/costs-report/?year=${year}`);
      setData(res.data);
    } catch (e) {
      setError(
        "Failed to load report: " +
          (e.response?.data ? JSON.stringify(e.response.data) : e.message)
      );
    } finally {
      setLoading(false);
    }
  }, [year]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const totalFixed = data ? data.months.reduce((s, r) => s + r.fixed_total, 0) : 0;
  const totalOnetime = data ? data.months.reduce((s, r) => s + r.onetime_total, 0) : 0;
  const totalCleaning = data ? data.months.reduce((s, r) => s + r.cleaning_total, 0) : 0;

  return (
    <Box>
      {/* Year selector */}
      <Box sx={{ display: "flex", gap: 2, alignItems: "center", mb: 3 }}>
        <TextField
          label="Year"
          type="number"
          size="small"
          value={year}
          onChange={(e) => setYear(Number(e.target.value))}
          inputProps={{ min: 2020, max: 2100 }}
          sx={{ width: 110 }}
        />
        {data && (
          <Typography variant="body2" color="text.secondary">
            Showing operational costs for <strong>{year}</strong>. Click a month row to expand individual items.
          </Typography>
        )}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && data && (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: "#2c5f8a" }}>
                <TableCell sx={{ color: "white", width: 36 }} />
                <TableCell sx={{ color: "white", fontWeight: "bold" }}>Month</TableCell>
                <TableCell align="right" sx={{ color: "white", fontWeight: "bold" }}>
                  Fixed / Recurring (€)
                </TableCell>
                <TableCell align="right" sx={{ color: "white", fontWeight: "bold" }}>
                  One-time (€)
                </TableCell>
                <TableCell align="right" sx={{ color: "white", fontWeight: "bold" }}>
                  Cleaning Staff (€)
                </TableCell>
                <TableCell align="right" sx={{ color: "white", fontWeight: "bold" }}>
                  Total (€)
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.months.map((row) => (
                <MonthRow key={row.month} row={row} />
              ))}

              {/* Annual total row */}
              <TableRow sx={{ backgroundColor: "#d4e6f7" }}>
                <TableCell />
                <TableCell sx={{ fontWeight: "bold", fontSize: 13 }}>
                  ANNUAL TOTAL {year}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: "bold" }}>
                  € {totalFixed.toFixed(2)}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: "bold" }}>
                  € {totalOnetime.toFixed(2)}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: "bold" }}>
                  € {totalCleaning.toFixed(2)}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: "bold", fontSize: 15 }}>
                  € {data.annual_total.toFixed(2)}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default CostsReportTab;
